import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dropdown,
  List,
  Typography,
  Empty,
  Spin,
  Space,
  Tooltip
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  StarOutlined,
  MessageOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import './NotificationBell.scss';

const { Text, Paragraph } = Typography;

const NotificationBell = observer(() => {
  const navigate = useNavigate();
  const { notificationStore, authStore } = useStores();
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Start polling when authenticated
  useEffect(() => {
    if (authStore.isAuthenticated) {
      notificationStore.startPolling();
    }

    return () => {
      notificationStore.stopPolling();
    };
  }, [authStore.isAuthenticated, notificationStore]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (dropdownOpen && authStore.isAuthenticated) {
      notificationStore.fetchNotifications();
    }
  }, [dropdownOpen, authStore.isAuthenticated, notificationStore]);

  const getNotificationIcon = (type) => {
    const icons = {
      ORDER_CREATED: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
      ORDER_CONFIRMED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      ORDER_SHIPPED: <CarOutlined style={{ color: '#1890ff' }} />,
      ORDER_DELIVERED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      ORDER_CANCELLED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      APPOINTMENT_CREATED: <CalendarOutlined style={{ color: '#1890ff' }} />,
      APPOINTMENT_CONFIRMED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      APPOINTMENT_CANCELLED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      CATEGORY_APPROVED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      CATEGORY_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      BREED_APPROVED: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      BREED_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      SUBSCRIPTION_EXPIRING: <WarningOutlined style={{ color: '#fa8c16' }} />,
      NEW_REVIEW: <StarOutlined style={{ color: '#fadb14' }} />,
      REVIEW_RESPONSE: <MessageOutlined style={{ color: '#1890ff' }} />,
      KYC_VERIFIED: <SafetyCertificateOutlined style={{ color: '#52c41a' }} />,
      KYC_REJECTED: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      SYSTEM: <InfoCircleOutlined style={{ color: '#1890ff' }} />
    };
    return icons[type] || <BellOutlined style={{ color: '#1890ff' }} />;
  };

  // Fallback URLs for different notification types (for admin notifications)
  const getActionUrl = (notification) => {
    // If action_url is set, use it
    if (notification.action_url) {
      return notification.action_url;
    }

    // Fallback based on notification type
    const fallbackUrls = {
      // Admin notifications
      'ADMIN_NEW_INQUIRY': '/admin/inquiries',
      'ADMIN_CATEGORY_SUGGESTION': '/admin/category-suggestions',
      'ADMIN_BREED_SUGGESTION': '/admin/category-suggestions',
      'ADMIN_SERVICE_PENDING': '/admin/services',
      'ADMIN_PRODUCT_PENDING': '/admin/products',
      'ADMIN_PARTNER_VERIFICATION': '/admin/partners',
      // User notifications
      'ORDER_CREATED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_CONFIRMED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_SHIPPED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_DELIVERED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'ORDER_CANCELLED': notification.metadata?.order_id ? `/orders/${notification.metadata.order_id}` : '/orders',
      'APPOINTMENT_CREATED': '/my-appointments',
      'APPOINTMENT_CONFIRMED': '/my-appointments',
      'APPOINTMENT_CANCELLED': '/my-appointments',
      'CATEGORY_APPROVED': '/my-pets',
      'CATEGORY_REJECTED': '/my-pets',
      'BREED_APPROVED': '/my-pets',
      'BREED_REJECTED': '/my-pets',
      'SUBSCRIPTION_EXPIRING': '/premium',
      'NEW_REVIEW': '/partner/reviews',
      'REVIEW_RESPONSE': '/partner/reviews',
      'KYC_VERIFIED': '/partner/dashboard',
      'KYC_REJECTED': '/partner/dashboard',
    };

    return fallbackUrls[notification.notification_type] || null;
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      await notificationStore.markAsRead(notification.id);
    }

    // Navigate to action URL (with fallback)
    const actionUrl = getActionUrl(notification);
    if (actionUrl) {
      setDropdownOpen(false);
      navigate(actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    await notificationStore.markAllAsRead();
  };

  const handleViewAll = () => {
    setDropdownOpen(false);
    navigate('/notifications');
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });

    return date.toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US');
  };

  const getNotificationTitle = (notification) => {
    // Use translated title if available
    if (i18n.language === 'el' && notification.title_el) {
      return notification.title_el;
    }
    return notification.title || notification.translated_title;
  };

  const getNotificationMessage = (notification) => {
    // Use translated message if available
    if (i18n.language === 'el' && notification.message_el) {
      return notification.message_el;
    }
    return notification.message || notification.translated_message;
  };

  const dropdownContent = (
    <div className="notification-dropdown">
      <div className="notification-header">
        <Text strong>{t('notifications.title')}</Text>
        {notificationStore.unreadCount > 0 && (
          <Tooltip title={t('notifications.markAllRead')}>
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
            />
          </Tooltip>
        )}
      </div>

      <div className="notification-list-container">
        {notificationStore.loading ? (
          <div className="notification-loading">
            <Spin />
          </div>
        ) : notificationStore.notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications.empty')}
            className="notification-empty"
          />
        ) : (
          <List
            className="notification-list"
            dataSource={notificationStore.notifications.slice(0, 5)}
            renderItem={(notification) => (
              <List.Item
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-content">
                  <div className="notification-icon">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="notification-text">
                    <Text strong className="notification-title">
                      {getNotificationTitle(notification)}
                    </Text>
                    <Paragraph
                      className="notification-message"
                      ellipsis={{ rows: 2 }}
                    >
                      {getNotificationMessage(notification)}
                    </Paragraph>
                    <Text type="secondary" className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                    </Text>
                  </div>
                  {!notification.is_read && (
                    <div className="notification-unread-dot" />
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </div>

      <div className="notification-footer">
        <Button type="link" onClick={handleViewAll}>
          {t('notifications.viewAll')}
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
    >
      <Badge count={notificationStore.unreadCount} overflowCount={99}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: '20px' }} />}
          className="notification-bell-button"
        />
      </Badge>
    </Dropdown>
  );
});

export default NotificationBell;
